import { useQuery } from 'react-query'
import {
  extractSubdomain,
  fetchTenantConfig,
  getCachedTenantConfig,
} from '../services/tenant.service'

/**
 * React Query hook for fetching tenant configuration
 * Provides proper loading states, error handling, and caching
 */
export const useTenantConfig = () => {
  const subdomain = extractSubdomain()

  // Check if we're on the login page
  const isOnLoginPage =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/login' || window.location.pathname.includes('/login'))

  // Demo public routes don't need tenant config (works on demo.* subdomain AND localhost)
  const DEMO_PUBLIC_PATHS = ['/signup', '/verify', '/creating', '/magic-link', '/login']
  const isDemoContext = (() => {
    const hostname = window.location.hostname
    if (hostname.split('.')[0] === 'demo') return true
    if (
      (hostname === 'localhost' || hostname === '127.0.0.1') &&
      import.meta.env.VITE_CORE_SHARED_STAFF_TENANT_ID
    ) {
      return true
    }
    return false
  })()
  const isDemoPublicRoute =
    isDemoContext &&
    typeof window !== 'undefined' &&
    DEMO_PUBLIC_PATHS.some(
      (p) => window.location.pathname === p || window.location.pathname.startsWith(p + '/')
    )

  const result = useQuery({
    queryKey: ['tenantConfig', subdomain],
    queryFn: () => {
      if (!subdomain) {
        // Return a default config for non-subdomain environments
        return Promise.resolve({
          tenants: { staff: null, client: null },
          corporate: null,
        })
      }
      return fetchTenantConfig(subdomain)
    },
    enabled: !!subdomain && !isDemoPublicRoute, // Skip fetch for demo subdomain public routes
    staleTime: 5 * 60 * 1000, // 5 minutes - tenant config doesn't change often
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: isOnLoginPage ? 0 : 1, // Don't retry on login page to prevent interference
    // Shorter timeout to prevent infinite loading
    retryDelay: 1000,
    // Use cached sessionStorage data as initial data for instant startup
    // React Query will still fetch fresh data in background (stale-while-revalidate)
    initialData: () => {
      if (!subdomain) {
        return {
          tenants: { staff: null, client: null },
          corporate: null,
        }
      }
      const cached = getCachedTenantConfig()
      // Use cached data for instant load - query will refresh in background
      return cached || undefined
    },
    // Mark cached initial data as stale to trigger background refresh
    initialDataUpdatedAt: () => {
      // Return 0 to immediately mark cached data as stale, triggering background fetch
      return 0
    },
    // Keep previous data while refetching
    keepPreviousData: true,
    // Don't retry on error for too long
    onError: (error) => {
      if (!isOnLoginPage) {
        console.error('Tenant config query failed:', error)
      }
      // On login page, fail silently to avoid console noise
    },
  })

  return result
}

/**
 * Hook to get specific tenant IDs from the config
 */
export const useTenantIds = () => {
  const { data: config, isLoading, error } = useTenantConfig()

  return {
    staffTenantId: config?.tenants?.staff || null,
    clientTenantId: config?.tenants?.client || null,
    corporateInfo: config?.corporate || null,
    isLoading,
    error,
  }
}
